import { useEffect } from "react";

import { FormInstance } from "antd/es/form";
import { isEqual } from "lodash-es";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { Group } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { ChildLayout, LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";

import { getSelectableNewDealDefaults } from "@app/util/deal/dealLockPickers";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { parseApiDate } from "@app/util/parsers";

const useDefaultValues = (
  actualSchema: LayoutSchemaField[],
  childLayout: ChildLayout | undefined,
  isCreateForm: boolean | undefined,
  fieldModel: FieldModel,
  form: FormInstance<any>,
  funnels: Funnel[],
  funnelStages: Record<Funnel["id"], Stage[]>,
  // Fields whose layout default must not be applied, because their current value — empty
  // included — was already decided somewhere else. Nothing here can tell a field its user
  // emptied on purpose from one that was never offered to them, so a form opened on values
  // entered earlier names the fields they were shown.
  skipLayoutDefaultsFor: Set<LayoutSchemaField["field"]> | undefined
) => {
  useEffect(() => {
    if (!isCreateForm) {
      return;
    }
    // a new deal can't start in a locked funnel or a read-only stage, whatever the layout says
    const selectable = (funnelId?: Funnel["id"], stageId?: Stage["id"]) =>
      getSelectableNewDealDefaults({ funnelId, stageId }, funnels, funnelStages);

    if (childLayout) {
      const defaults = selectable(childLayout.funnel?.id, childLayout.stage?.id);
      if (!form.getFieldValue("funnelId")) {
        form.setFieldValue("funnelId", defaults.funnelId);
      }
      if (!form.getFieldValue("stageId")) {
        form.setFieldValue("stageId", defaults.stageId);
      }
    }

    if (childLayout && !form.getFieldValue("activityTypeId")) {
      form.setFieldValue("activityTypeId", childLayout.crmActivityType?.id);
    }

    actualSchema.forEach((schemaField) => {
      const field = fieldModel.getByPlatformName(schemaField.field);
      if (
        !field?.isDefaultValueAllowed ||
        !schemaField.defaultValue?.length ||
        skipLayoutDefaultsFor?.has(schemaField.field)
      ) {
        return;
      }

      if (field.hasFeature(FieldFeature.GROUP_FIELD)) {
        if (!form.getFieldValue(field.name)) {
          form.setFieldValue(field.name, new Set(schemaField.defaultValue as Group["id"][]));
        }
      } else if (field.hasFeature(FieldFeature.FUNNEL_FIELD) && !childLayout) {
        if (!form.getFieldValue("funnelId")) {
          form.setFieldValue(
            "funnelId",
            selectable(schemaField.defaultValue[0] as Funnel["id"]).funnelId
          );
        }
      } else if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD) && !childLayout) {
        if (!form.getFieldValue("activityTypeId")) {
          form.setFieldValue("activityTypeId", schemaField.defaultValue[0] as ActivityType["id"]);
        }
      } else if (schemaField.field === DealFieldName.CLOSING_DATE) {
        if (!form.getFieldValue(DealFieldName.CLOSING_DATE)) {
          form.setFieldValue(
            DealFieldName.CLOSING_DATE,
            parseApiDate(schemaField.defaultValue[0] as string)
          );
        }
      } else if (field.hasFeature(FieldFeature.STAGE_FIELD) && !childLayout) {
        const defaultStageId = schemaField.defaultValue[0] as Stage["id"];
        const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
          funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === defaultStageId)
        );
        if (funnelIdString && !form.getFieldValue("stageId") && !form.getFieldValue("funnelId")) {
          const defaults = selectable(parseInt(funnelIdString), defaultStageId);
          form.setFieldValue("funnelId", defaults.funnelId);
          form.setFieldValue("stageId", defaults.stageId);
        }
      } else if (isCustomField(field)) {
        const definition = field.customFieldData;

        if (!form.getFieldValue(["customFields", String(definition.id)])?.value) {
          form.setFieldValue(["customFields", String(definition.id)], {
            customField: { id: definition.id },
            value: schemaField.defaultValue,
          });
        }
      } else if (!form.getFieldValue(schemaField.field)) {
        form.setFieldValue(schemaField.field, schemaField.defaultValue[0]);
      }
    });

    return () => {
      if (!isCreateForm) {
        return;
      }
      actualSchema.forEach((schemaField) => {
        const field = fieldModel.getByPlatformName(schemaField.field);
        // Skipped here too, and for the same reason: this strips a value that still equals the
        // default, which on a skipped field is the user's own value rather than one we seeded.
        if (
          !field?.isDefaultValueAllowed ||
          !schemaField.defaultValue?.length ||
          skipLayoutDefaultsFor?.has(schemaField.field)
        ) {
          return;
        }
        if (isCustomField(field)) {
          const definition = field.customFieldData;
          if (
            isEqual(
              form.getFieldValue(["customFields", String(definition.id)])?.value,
              schemaField.defaultValue
            )
          ) {
            form.setFieldValue(["customFields", String(definition.id)], {
              customField: { id: definition.id },
              value: undefined,
            });
          }
        } else if (field.hasFeature(FieldFeature.GROUP_FIELD)) {
          if (
            isEqual(
              form.getFieldValue("groups"),
              new Set(schemaField.defaultValue as Group["id"][])
            )
          ) {
            form.setFieldValue("groups", undefined);
          }
        } else if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD)) {
          if (form.getFieldValue("activityTypeId") === schemaField.defaultValue[0]) {
            form.setFieldValue("activityTypeId", undefined);
          }
        } else if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
          if (
            form.getFieldValue("funnelId") === schemaField.defaultValue[0] &&
            !form.isFieldTouched("stageId")
          ) {
            form.setFieldValue("funnelId", undefined);
          }
        } else if (field.hasFeature(FieldFeature.STAGE_FIELD)) {
          const defaultStageId = schemaField.defaultValue[0] as Stage["id"];
          const stageId = form.getFieldValue("stageId");
          // a read-only default stage seeds only its funnel
          const funnelSeededAlone =
            !stageId &&
            !form.isFieldTouched("funnelId") &&
            !!funnelStages[form.getFieldValue("funnelId")]?.some(({ id }) => id === defaultStageId);
          if (stageId === defaultStageId || funnelSeededAlone) {
            form.setFieldValue("stageId", undefined);
            form.setFieldValue("funnelId", undefined);
          }
        } else if (schemaField.field === DealFieldName.CLOSING_DATE) {
          if (
            isEqual(
              form.getFieldValue(DealFieldName.CLOSING_DATE),
              parseApiDate(schemaField.defaultValue[0] as string)
            )
          ) {
            form.setFieldValue(DealFieldName.CLOSING_DATE, undefined);
          }
        } else if (isEqual(form.getFieldValue(schemaField.field), schemaField.defaultValue[0])) {
          form.setFieldValue(schemaField.field, undefined);
        }
      });
    };
  }, [
    actualSchema,
    childLayout,
    isCreateForm,
    fieldModel,
    form,
    funnels,
    funnelStages,
    skipLayoutDefaultsFor,
  ]);
};

export default useDefaultValues;
